/**
 * Lineage Comparison Panel (DC-873)
 *
 * Compares lineage across journey versions/snapshots.
 * Shows differences between two points in time.
 *
 * Features:
 * - Snapshot selection dropdown
 * - Side-by-side statistics comparison
 * - Added/removed events visualization
 *
 * @see Documentation/Development/Design Docs/Sprint 11/Data_Lineage_and_Provenance_Design.md
 */

import React, { useState, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import './lineage-comparison.css';

// ============================================================================
// TYPES
// ============================================================================

interface LineageSnapshot {
  snapshot_id: string;
  journey_id: string;
  snapshot_name: string | null;
  snapshot_reason: string | null;
  total_findings: number;
  total_syntheses: number;
  total_recommendations: number;
  total_valuations: number;
  stations_involved: string[];
  value_total: number;
  created_at: string;
  created_by: string | null;
}

interface LineageEvent {
  event_id: string;
  event_type: string;
  event_source: string;
  content_payload: Record<string, unknown>;
  created_at: string;
}

interface ComparisonResult {
  added: LineageEvent[];
  removed: LineageEvent[];
  snapshot1Stats: {
    findings: number;
    syntheses: number;
    recommendations: number;
    valuations: number;
  };
  snapshot2Stats: {
    findings: number;
    syntheses: number;
    recommendations: number;
    valuations: number;
  };
}

interface LineageComparisonPanelProps {
  journeyId: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const LineageComparisonPanel: React.FC<LineageComparisonPanelProps> = ({
  journeyId,
}) => {
  const { authFetch } = useApiClient();
  const [snapshots, setSnapshots] = useState<LineageSnapshot[]>([]);
  const [selectedSnapshot1, setSelectedSnapshot1] = useState<string | null>(null);
  const [selectedSnapshot2, setSelectedSnapshot2] = useState<string | null>(null);
  const [comparison, setComparison] = useState<ComparisonResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available snapshots
  useEffect(() => {
    const fetchSnapshots = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authFetch(`/api/lineage/${journeyId}/snapshots`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch snapshots: ${response.status}`);
        }

        const data = await response.json();
        setSnapshots(data.snapshots || []);
      } catch (err) {
        console.error('[LineageComparison] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load snapshots');
      } finally {
        setLoading(false);
      }
    };

    if (journeyId) {
      fetchSnapshots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [journeyId]);

  // Compare snapshots when both are selected
  const handleCompare = async () => {
    if (!selectedSnapshot1 || !selectedSnapshot2) return;

    setComparing(true);
    setError(null);

    try {
      const response = await authFetch(
        `/api/lineage/${journeyId}/compare?snapshot1=${selectedSnapshot1}&snapshot2=${selectedSnapshot2}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to compare snapshots: ${response.status}`);
      }

      const data = await response.json();
      setComparison(data);
    } catch (err) {
      console.error('[LineageComparison] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to compare snapshots');
    } finally {
      setComparing(false);
    }
  };

  // Create a new snapshot
  const handleCreateSnapshot = async () => {
    const name = window.prompt('Enter snapshot name (optional):');
    const reason = window.prompt('Reason for snapshot:', 'manual');

    try {
      const response = await authFetch(`/api/lineage/${journeyId}/snapshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          snapshot_name: name || undefined,
          snapshot_reason: reason || 'manual',
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create snapshot: ${response.status}`);
      }

      const data = await response.json();
      alert(`Snapshot created: ${data.snapshot_id}`);

      // Refresh snapshots list
      const listResponse = await authFetch(`/api/lineage/${journeyId}/snapshots`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (listResponse.ok) {
        const listData = await listResponse.json();
        setSnapshots(listData.snapshots || []);
      }
    } catch (err) {
      console.error('[LineageComparison] Error creating snapshot:', err);
      alert(`Failed to create snapshot: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const _getSnapshot = (id: string | null): LineageSnapshot | undefined => {
    return snapshots.find(s => s.snapshot_id === id);
  };

  if (loading) {
    return (
      <div className="lineage-comparison-panel loading">
        <div className="loading-spinner">Loading snapshots...</div>
      </div>
    );
  }

  return (
    <div className="lineage-comparison-panel">
      {/* Header */}
      <div className="comparison-header">
        <h3>Compare Lineage Snapshots</h3>
        <button className="create-snapshot-btn" onClick={handleCreateSnapshot}>
          + New Snapshot
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {snapshots.length < 2 ? (
        <div className="no-snapshots">
          <span className="no-snapshots-icon">📸</span>
          <p>You need at least 2 snapshots to compare.</p>
          <p className="hint">
            {snapshots.length === 0
              ? 'Create your first snapshot using the button above.'
              : 'Create another snapshot to enable comparison.'}
          </p>
        </div>
      ) : (
        <>
          {/* Snapshot Selection */}
          <div className="snapshot-selection">
            <div className="snapshot-select">
              <label>Earlier Snapshot</label>
              <select
                value={selectedSnapshot1 || ''}
                onChange={(e) => setSelectedSnapshot1(e.target.value || null)}
              >
                <option value="">Select snapshot...</option>
                {snapshots.map((s) => (
                  <option key={s.snapshot_id} value={s.snapshot_id}>
                    {s.snapshot_name || formatDate(s.created_at)} ({s.snapshot_reason})
                  </option>
                ))}
              </select>
            </div>

            <div className="comparison-arrow">→</div>

            <div className="snapshot-select">
              <label>Later Snapshot</label>
              <select
                value={selectedSnapshot2 || ''}
                onChange={(e) => setSelectedSnapshot2(e.target.value || null)}
              >
                <option value="">Select snapshot...</option>
                {snapshots.map((s) => (
                  <option key={s.snapshot_id} value={s.snapshot_id}>
                    {s.snapshot_name || formatDate(s.created_at)} ({s.snapshot_reason})
                  </option>
                ))}
              </select>
            </div>

            <button
              className="compare-btn"
              onClick={handleCompare}
              disabled={!selectedSnapshot1 || !selectedSnapshot2 || comparing}
            >
              {comparing ? 'Comparing...' : 'Compare'}
            </button>
          </div>

          {/* Comparison Results */}
          {comparison && (
            <div className="comparison-results">
              {/* Statistics Comparison */}
              <div className="stats-comparison">
                <div className="stats-column">
                  <h4>Earlier Snapshot</h4>
                  <div className="stat-grid">
                    <div className="stat-item">
                      <span className="stat-label">Findings</span>
                      <span className="stat-value">{comparison.snapshot1Stats.findings}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Syntheses</span>
                      <span className="stat-value">{comparison.snapshot1Stats.syntheses}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Recommendations</span>
                      <span className="stat-value">{comparison.snapshot1Stats.recommendations}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Valuations</span>
                      <span className="stat-value">{comparison.snapshot1Stats.valuations}</span>
                    </div>
                  </div>
                </div>

                <div className="stats-divider">
                  <div className="delta-badge added">
                    +{comparison.added.length} added
                  </div>
                  <div className="delta-badge removed">
                    -{comparison.removed.length} removed
                  </div>
                </div>

                <div className="stats-column">
                  <h4>Later Snapshot</h4>
                  <div className="stat-grid">
                    <div className="stat-item">
                      <span className="stat-label">Findings</span>
                      <span className="stat-value">
                        {comparison.snapshot2Stats.findings}
                        <DeltaIndicator
                          delta={comparison.snapshot2Stats.findings - comparison.snapshot1Stats.findings}
                        />
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Syntheses</span>
                      <span className="stat-value">
                        {comparison.snapshot2Stats.syntheses}
                        <DeltaIndicator
                          delta={comparison.snapshot2Stats.syntheses - comparison.snapshot1Stats.syntheses}
                        />
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Recommendations</span>
                      <span className="stat-value">
                        {comparison.snapshot2Stats.recommendations}
                        <DeltaIndicator
                          delta={comparison.snapshot2Stats.recommendations - comparison.snapshot1Stats.recommendations}
                        />
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Valuations</span>
                      <span className="stat-value">
                        {comparison.snapshot2Stats.valuations}
                        <DeltaIndicator
                          delta={comparison.snapshot2Stats.valuations - comparison.snapshot1Stats.valuations}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Changes */}
              <div className="event-changes">
                {comparison.added.length > 0 && (
                  <div className="change-section added">
                    <h4>Added Events ({comparison.added.length})</h4>
                    <div className="event-list">
                      {comparison.added.slice(0, 10).map((event) => (
                        <div key={event.event_id} className="change-event">
                          <span className="event-type">{event.event_type}</span>
                          <span className="event-source">{event.event_source}</span>
                        </div>
                      ))}
                      {comparison.added.length > 10 && (
                        <div className="more-events">
                          +{comparison.added.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {comparison.removed.length > 0 && (
                  <div className="change-section removed">
                    <h4>Removed Events ({comparison.removed.length})</h4>
                    <div className="event-list">
                      {comparison.removed.slice(0, 10).map((event) => (
                        <div key={event.event_id} className="change-event">
                          <span className="event-type">{event.event_type}</span>
                          <span className="event-source">{event.event_source}</span>
                        </div>
                      ))}
                      {comparison.removed.length > 10 && (
                        <div className="more-events">
                          +{comparison.removed.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {comparison.added.length === 0 && comparison.removed.length === 0 && (
                  <div className="no-changes">
                    <span className="no-changes-icon">✓</span>
                    <p>No changes between these snapshots</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Helper component for delta indicators
const DeltaIndicator: React.FC<{ delta: number }> = ({ delta }) => {
  if (delta === 0) return null;
  const isPositive = delta > 0;
  return (
    <span className={`delta-indicator ${isPositive ? 'positive' : 'negative'}`}>
      {isPositive ? '+' : ''}{delta}
    </span>
  );
};

export default LineageComparisonPanel;
